/**
 * CompaniesSection Component
 *
 * Displays and manages company associations for a contact.
 * Supports add/delete operations with company search.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { contactsApi } from '@/services/contactsApi';
import type { ContactCompany, ContactRoleInput, Company } from '../types';
import { useDebounce } from '@/hooks';

interface CompaniesSectionProps {
  companies: ContactCompany[];
  contactId: string;
  onUpdate: () => void;
  onError: (error: string) => void;
  onCompanyClick?: (companyId: string) => void;
}

export default function CompaniesSection({
  companies,
  contactId,
  onUpdate,
  onError,
  onCompanyClick,
}: CompaniesSectionProps) {
  const { t } = useTranslation('people');
  const [showAdd, setShowAdd] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState<ContactRoleInput>({
    company_id: '',
    title: '',
    is_current: true,
    is_primary: false,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Company[]>([]);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');

  const debouncedSearch = useDebounce(searchQuery, 300);

  // Search companies when query changes
  const searchCompanies = useCallback(async () => {
    if (!debouncedSearch.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      // Use semantic search to find companies
      const response = await contactsApi.searchSemantic(debouncedSearch, {
        limit: 10,
        mode: 'hybrid',
        includeCompanies: true,
      });
      // Filter for companies only and convert to Company format
      const companyResults: Company[] = response.results
        .filter(r => r.entity_type === 'company')
        .map(r => ({
          id: r.id,
          name: r.name || r.full_name || '',
          domain: undefined,
          aliases: [],
          industry: undefined,
          size: undefined,
          website: undefined,
          linkedin_url: undefined,
          description: undefined,
          logo_url: undefined,
          tags: [],
          contact_count: 0,
          created_at: '',
          updated_at: '',
        }));
      setSearchResults(companyResults);
    } catch (err) {
      console.error('Company search failed:', err);
      setSearchResults([]);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    searchCompanies();
  }, [searchCompanies]);

  const handleAdd = async () => {
    if (!newCompanyForm.company_id) return;

    try {
      await contactsApi.addRole(contactId, newCompanyForm);
      onUpdate();
      setShowAdd(false);
      setNewCompanyForm({
        company_id: '',
        title: '',
        is_current: true,
        is_primary: false,
      });
      setSearchQuery('');
      setSelectedCompanyName('');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to add company');
    }
  };

  const handleDelete = async (roleId: string, companyName: string) => {
    if (!confirm(`Remove association with ${companyName}?`)) return;

    try {
      await contactsApi.deleteRole(contactId, roleId);
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to remove company association');
    }
  };

  const selectCompany = (company: Company) => {
    setNewCompanyForm({ ...newCompanyForm, company_id: company.id });
    setSelectedCompanyName(company.name);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="mb-6">
      <label className="block text-xs font-medium text-gray-500 uppercase mb-2">{t('sections.companies.label')}</label>
      <div className="space-y-2">
        {companies && companies.length > 0 ? (
          companies.map((role) => (
            <div key={role.role_id} className="flex items-center gap-2 text-sm group bg-gray-50 p-2 rounded-lg">
              <button
                onClick={() => handleDelete(role.role_id, role.company_name)}
                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                title={t('sections.companies.remove_from_company')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex-1">
                <span
                  className={`font-medium text-gray-900 ${onCompanyClick ? 'hover:text-blue-600 cursor-pointer' : ''}`}
                  onClick={() => onCompanyClick?.(role.company_id)}
                >
                  {role.company_name}
                </span>
                {role.title && (
                  <span className="text-gray-500 ml-2">• {role.title}</span>
                )}
              </div>
              <div className="flex gap-1">
                {role.is_current && (
                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">{t('sections.companies.current')}</span>
                )}
                {role.is_primary && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{t('sections.companies.primary')}</span>
                )}
              </div>
            </div>
          ))
        ) : (
          <span className="text-gray-400 text-sm">{t('sections.companies.no_companies')}</span>
        )}

        {/* Add Company Form */}
        {showAdd ? (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-2">
            {/* Company Search */}
            <div className="relative">
              {selectedCompanyName ? (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded">
                  <span className="text-sm">{selectedCompanyName}</span>
                  <button
                    onClick={() => {
                      setNewCompanyForm({ ...newCompanyForm, company_id: '' });
                      setSelectedCompanyName('');
                    }}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('sections.companies.search_placeholder')}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-auto">
                      {searchResults.map((company) => (
                        <button
                          key={company.id}
                          onClick={() => selectCompany(company)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                        >
                          {company.name}
                          {company.domain && (
                            <span className="text-gray-400 ml-2">({company.domain})</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Title */}
            <input
              type="text"
              value={newCompanyForm.title || ''}
              onChange={(e) => setNewCompanyForm({ ...newCompanyForm, title: e.target.value })}
              placeholder={t('sections.companies.job_title_placeholder')}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            {/* Options */}
            <div className="flex gap-4">
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={newCompanyForm.is_current}
                  onChange={(e) => setNewCompanyForm({ ...newCompanyForm, is_current: e.target.checked })}
                />
                {t('sections.companies.current')}
              </label>
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={newCompanyForm.is_primary}
                  onChange={(e) => setNewCompanyForm({ ...newCompanyForm, is_primary: e.target.checked })}
                />
                {t('sections.companies.primary')}
              </label>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                disabled={!newCompanyForm.company_id}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {t('sections.companies.add')}
              </button>
              <button
                onClick={() => {
                  setShowAdd(false);
                  setNewCompanyForm({
                    company_id: '',
                    title: '',
                    is_current: true,
                    is_primary: false,
                  });
                  setSearchQuery('');
                  setSelectedCompanyName('');
                }}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded"
              >
                {t('sections.companies.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAdd(true)}
            className="mt-1 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('sections.companies.add_company')}
          </button>
        )}
      </div>
    </div>
  );
}
